import ChangedField from "./ChangedField";

import CheckedIcon from "../../../assets/layerlist/checked.svg?react";
import UncheckedIcon from "../../../assets/layerlist/unchecked.svg?react";
import LocationIcon from "../../../assets/location-pin.svg?react";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import "./EditedFeaturePanel.css";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import { GetCodedValueAndAlias, getLayerName, isMainFeatureByLayerId, zoomTo } from "../../../utils/arcgisUtils";
import { toast } from "react-toastify";
import { useContext, useEffect, useState } from "react";
import {
    addGraphicsToJobsGraphicsLayer,
    objectsShouldValidate,
    removeGraphicsFromJobsGraphicsLayer,
} from "../../../utils/jobsUtils";
import { AppContext, JobContext } from "../../../Context";
import { LeftPanelTabs } from "../../../constants/Panels";
import { setLeftPanelActiveTab } from "../../../store/appSlice";
import { useDispatch } from "react-redux";
import { FeatureObject, FeatureWithChanges } from "../../../utils/newVersioningUtils";
import ValidationResult from "./ValidationResult";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface EditedFeaturePanelForNewJobProps {
    editedFeature: FeatureWithChanges;
    // restoreFeature: (
    //     layerId: number,
    //     object: { mainFeature: FeatureObject; relatedFeatures: RelatedFeatures[] }
    // ) => void;
    addFeatureToJob: (featureToAdd: FeatureWithChanges) => void;
    removeFeatureFromJob: (featureToRemove: FeatureWithChanges) => void;
    featuresAddedToJob: FeatureWithChanges[];
    // hideRestore?: boolean;
    cannotAddFeatureToJob?: boolean;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const EditedFeaturePanelForNewJob = (props: EditedFeaturePanelForNewJobProps) => {
    const dispatch = useDispatch();

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [isOriginalFeatureVisible, setIsOriginalFeatureVisible] = useState(false);
    const appContext = useContext(AppContext);
    const jobContext = useContext(JobContext);
    const validateObjects = objectsShouldValidate(jobContext?.selectedJob.value?.attributes.jobb_type);

    const getTitle = () => {
        let sjomerke_nr = "";
        for (const key in props.editedFeature.mainFeature.versionGraphic?.attributes) {
            if (key === "sjomerke_nr") {
                sjomerke_nr = props.editedFeature.mainFeature.versionGraphic?.attributes[key];
                break;
            }
        }
        if (sjomerke_nr === "" || sjomerke_nr === null) {
            sjomerke_nr =
                props.editedFeature.mainFeature.versionGraphic?.attributes["navn"] ??
                "ObjId:" +
                    props.editedFeature.mainFeature.objectId +
                    " - " +
                    getLayerName(props.editedFeature.mainFeature.layerId);
        }

        return sjomerke_nr;
    };

    const getChanges = () => {
        const changes: React.JSX.Element[] = [];
        if (props.editedFeature.mainFeature.type === "update" || props.editedFeature.mainFeature.type === "insert") {
            for (const field of props.editedFeature.mainFeature.changedFieldsDefault ?? []) {
                changes.push(
                    <ChangedField editedFeature={props.editedFeature.mainFeature} field={field} fromOperative={false} />
                );
            }
        }

        return changes;
    };

    const getRelatedFeatures = () => {
        const relatedChanges: React.JSX.Element[] = [];
        const changesByLayer: { [key: number]: FeatureObject[] } = {};
        for (const relatedFeature of props.editedFeature.relatedFeatures) {
            if (!changesByLayer[relatedFeature.layerId]) {
                changesByLayer[relatedFeature.layerId] = [];
            }
            changesByLayer[relatedFeature.layerId].push(relatedFeature);
        }

        const mainFeatureElements: React.JSX.Element[] = [];
        const relatedFeatureElements: React.JSX.Element[] = [];

        for (const layerId in changesByLayer) {
            if (isMainFeatureByLayerId(Number(layerId))) {
                const layerName = getLayerName(Number.parseInt(layerId));

                for (const mainFeature of changesByLayer[layerId]) {
                    const collapsible = (
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title={
                                layerName +
                                " - " +
                                (mainFeature.versionGraphic?.attributes.sjomerke_nr ??
                                    "ObjId:" + mainFeature.versionGraphic?.attributes.objectid)
                            }
                            messageWhenEmpty="Ingen endringer funnet"
                        >
                            {mainFeature.changedFieldsDefault?.map((field) => (
                                <ChangedField
                                    key={`${mainFeature.layerId}-${mainFeature.objectId}-related-${field}`}
                                    editedFeature={mainFeature}
                                    field={field}
                                    fromOperative={false}
                                />
                            ))}
                        </Collapsible>
                    );
                    mainFeatureElements.push(collapsible);
                }
            } else {
                relatedChanges.push(
                    <div key={layerId} className="flex-row w-100 space-between">
                        <span className="job-card-key">{getLayerName(Number.parseInt(layerId))}</span>
                        {/* <span className="job-card-value bold">
                            Antall: {changesByLayer[layerId].length}
                        </span> */}
                    </div>
                );
            }
        }
        if (relatedFeatureElements.length > 0) {
            relatedChanges.push(...relatedFeatureElements);
        }
        if (mainFeatureElements.length > 0) {
            relatedChanges.push(...mainFeatureElements);
        }

        if (relatedChanges.length === 0) {
            return null;
        } else {
            return (
                <Collapsible
                    isOpen={false}
                    setIsOpen={() => {}}
                    title="Relaterte objekter"
                    className="edited-feature-panel-collapsible"
                    messageWhenEmpty="Ingen endringer funnet"
                >
                    {relatedChanges}
                </Collapsible>
            );
        }
    };

    const selectFeature = () => {
        props.addFeatureToJob(props.editedFeature);
    };

    const unselectFeature = () => {
        props.removeFeatureFromJob(props.editedFeature);
    };

    const zoomToFeature = async () => {
        if (!props.editedFeature) return;

        const mainFeature = props.editedFeature.mainFeature.versionGraphic;
        if (!mainFeature) return;

        props.clearHighlightedFeatures();
        zoomTo(mainFeature, props.storeHighlightHandle, "temporary");

        appContext?.shownFeature.set(mainFeature);

        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
    };

    const isSelected = () => {
        return props.featuresAddedToJob.some((feature) => {
            return (
                feature.mainFeature.layerId === props.editedFeature.mainFeature.layerId &&
                feature.mainFeature.objectId === props.editedFeature.mainFeature.objectId
            );
        });
    };

    const showHideFeature = () => {
        if (isOriginalFeatureVisible) {
            setIsOriginalFeatureVisible(false);
            const graphicsToRemove: Graphic[] = [];

            if (props.editedFeature.mainFeature.defaultGraphic) {
                graphicsToRemove.push(props.editedFeature.mainFeature.defaultGraphic);
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.defaultGraphic) {
                    graphicsToRemove.push(relatedFeature.defaultGraphic);
                }
            }
            removeGraphicsFromJobsGraphicsLayer(graphicsToRemove);
        } else {
            const graphicsToAdd: Graphic[] = [];
            if (props.editedFeature.mainFeature.defaultGraphic) {
                graphicsToAdd.push(props.editedFeature.mainFeature.defaultGraphic);
            } else {
                console.warn("No original graphic found for edited feature.");
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.defaultGraphic) {
                    graphicsToAdd.push(relatedFeature.defaultGraphic);
                }
            }
            addGraphicsToJobsGraphicsLayer(graphicsToAdd);
            setIsOriginalFeatureVisible(true);
        }
    };

    useEffect(() => {
        return () => {
            if (isOriginalFeatureVisible) {
                showHideFeature();
            }
        };
    }, [isOriginalFeatureVisible]);

    useEffect(() => {
        if (props.editedFeature.mainFeature.versionGraphic) {
            symbolUtils.getDisplayedSymbol(props.editedFeature.mainFeature.versionGraphic).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 48 }).then((symbolElement) => {
                        if (!symbolElement) return;
                        setSymbolHtmlElement(symbolElement);
                    });
                } else {
                    setSymbolHtmlElement(null);
                }
            });
        } else {
            setSymbolHtmlElement(null);
        }
    }, [props.editedFeature]);

    return (
        <div className="edited-feature-panel">
            <div className="edited-feature-panel-header">
                {symbolHtmlElement?.outerHTML && (
                    <div
                        className="search-item-symbol"
                        dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}
                    ></div>
                )}
                <button
                    className="nfs-button no-flex fit-content"
                    style={{ padding: "0rem 0.5rem" }}
                    onClick={showHideFeature}
                >
                    {isOriginalFeatureVisible ? "Skjul" : "Vis"}
                </button>
                <Checkbox
                    checked={isSelected()}
                    onChange={() => {
                        if (isSelected()) {
                            unselectFeature();
                        } else {
                            if (!props.cannotAddFeatureToJob) {
                                selectFeature();
                            } else {
                                toast.warning(
                                    "En jobb kan kun inneholde ett nautisk objekt, med unntak av erstatting ved nedleggelse eller fjerning."
                                );
                            }
                        }
                    }}
                    id={`edited-feature-panel-checkbox ${props.editedFeature.mainFeature.versionGraphic?.attributes.objectid}`}
                    checkedIcon={CheckedIcon}
                    uncheckedIcon={UncheckedIcon}
                />
                <span>{getTitle()}</span>
                <button className="empty-button" onClick={zoomToFeature}>
                    <LocationIcon />
                </button>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Status</span>
                <span className="job-card-value bold">
                    {
                        GetCodedValueAndAlias(
                            props.editedFeature.mainFeature.versionGraphic?.layer,
                            "nfs_status",
                            props.editedFeature.mainFeature.versionGraphic?.attributes.nfs_status
                        )[1]
                    }
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Navn</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.navn}
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Sted</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.sted}
                </span>
            </div>

            {validateObjects && <ValidationResult validationResult={props.editedFeature.validationResult} />}

            {/* {getRelatedFeatures()} */}
            <br />
            <span>Viser endringer sammenlignet med Planroot</span>
            <Collapsible
                isOpen={false}
                setIsOpen={() => {}}
                title="Vis endringer"
                className="edited-feature-panel-collapsible"
                messageWhenEmpty="Ingen endringer funnet"
            >
                {getChanges()}
            </Collapsible>
            {getRelatedFeatures()}

            {/* {!props.hideRestore && (
                <div className="flex-row center w-100">
                    <NfsButton onClick={restoreObject}>
                        <span className="flex-row center small-gap">
                            <RestoreIcon className="restore-icon" />
                            <span className="restore-text">Forkast endringer</span>
                        </span>
                    </NfsButton>
                </div>
            )} */}
        </div>
    );
};

export default EditedFeaturePanelForNewJob;
